import React, { useMemo } from "react";
import PropTypes from "prop-types";

import { UseSelectedWorkspace } from "../../hooks/company";
import useLeaderboardData from "../LeaderBoard/LeaderboardTable/data";
import { resolveCardWindow } from "../../helpers/cardWindow";
import Podium from "../../pages/CompanyView/Podium";
import CardBusy from "./CardBusy";
import "./cardScope.css";

/**
 * Dashboard podium pedestals fed by the SAME daily-score window as
 * LeaderboardTable — including custom from/to, quarter, and YTD.
 *
 * Classic LiveLeaderboard only understands named period buckets and was
 * forced to "This month" whenever the navbar sat on Custom, so the list and
 * the pedestals disagreed. This wrapper keeps the 3D podium UI and ranks the
 * top three from the clipped window.
 */

const DashboardPodium = ({ top3 }) => {
  const ref = React.useRef(null);
  const [box, setBox] = React.useState({ w: 0, h: 0 });
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      if (entries[0]) setBox({ w: entries[0].contentRect.width, h: entries[0].contentRect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const w = box.w || 400;
  const h = box.h || 520;
  const scale = Math.min(1.1, (w * 0.97) / 496);
  const headZone = 134 * scale + 38;
  const targetCyl = h * 0.92 - headZone;
  let pedestal = ((targetCyl * 150) / (200 * scale) - 64) / 96;
  pedestal = Math.max(1, Math.min(6, pedestal));
  const data = top3.map((u) => ({
    name: u.name || "Player",
    points: Math.round(Number(u.points) || 0),
    image: u.image,
  }));
  return (
    <div
      ref={ref}
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      <Podium top3={data} scale={scale} pedestal={pedestal} />
    </div>
  );
};

DashboardPodium.propTypes = {
  top3: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    points: PropTypes.number,
    image: PropTypes.string,
  })).isRequired,
};

const nameOf = (row) =>
  [row.firstName, row.lastName].filter(Boolean).join(" ").trim()
  || row.name
  || row.email
  || "Player";

/**
 * Resolve which workspaces the podium should rank — mirror the leaderboard
 * auto-across rules so both cards always share one roster.
 */
const resolveWorkspaceIds = ({ settings, workspaceId, boardWs, inheritWorkspaceIds }) => {
  const combined = Array.isArray(inheritWorkspaceIds) ? inheritWorkspaceIds.filter(Boolean).map(String) : [];
  const picked = (settings?.workspaceIds || []).filter(Boolean).map(String);
  const across = !!settings?.across;
  const autoAcross = !across && combined.length >= 2;

  if (across) {
    if (picked.length) return picked;
    if (boardWs && boardWs !== "all") return [String(boardWs)];
    if (combined.length) return combined;
    return workspaceId ? [String(workspaceId)] : [];
  }
  if (autoAcross) {
    if (boardWs && boardWs !== "all") return [String(boardWs)];
    return combined;
  }
  if (picked.length) return picked;
  return workspaceId ? [String(workspaceId)] : [];
};

const ScopedPodium = ({
  settings = {},
  workspaceId = null,
  boardWs = null,
  boardWindow = null,
  boardCustom = null,
  inheritWorkspaceIds = [],
  periodIdx = 3,
}) => {
  const selected = UseSelectedWorkspace();
  const ownId = workspaceId || selected?._id || null;
  const settingsWsKey = (settings?.workspaceIds || []).map(String).join("|");
  const inheritKey = (inheritWorkspaceIds || []).map(String).join("|");
  const across = !!settings?.across;

  const workspaceIds = useMemo(
    () => resolveWorkspaceIds({
      settings,
      workspaceId: ownId,
      boardWs,
      inheritWorkspaceIds,
    }),
    // settings object identity changes often; keys above are the real inputs
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsWsKey, across, ownId, boardWs, inheritKey],
  );

  const { window: bucket, custom: resolvedCustom } = resolveCardWindow(settings, {
    window: boardWindow != null ? boardWindow : periodIdx,
    custom: boardCustom,
  });
  const custom = bucket === "custom" ? (resolvedCustom || boardCustom) : null;

  const { rows, yetiPts, loading, refreshing } = useLeaderboardData(
    workspaceIds,
    bucket,
    settings.metricPlan || null,
    custom,
  );

  const top3 = useMemo(() => {
    const ranked = [...(rows || [])]
      .map((row) => ({
        name: nameOf(row),
        points: Number(row.pts) || 0,
        image: row.image || "",
      }));
    const yPts = Math.round(Number(yetiPts) || 0);
    /* Classic podium includes The Yeti when he ranks in the top three —
       list already does; pedestals must match. */
    if (yPts > 0 || ranked.length > 0) {
      ranked.push({ name: "The Yeti", points: yPts, image: "" });
    }
    return ranked
      .sort((a, b) => (Number(b.points) || 0) - (Number(a.points) || 0))
      .slice(0, 3);
  }, [rows, yetiPts]);

  const cold = loading && !top3.length;
  const soft = !!refreshing && !cold;

  /* Same white card shell as classic LeaderBoard podium — custom/quarter
     ScopedPodium used to paint pedestals on the grid with no background. */
  return (
    <div
      className={`gmbw-fill cs-wrap${soft ? " cs-is-refreshing" : ""}`}
      style={{
        position: "relative",
        minHeight: 220,
        height: "100%",
        background: "#fff",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        padding: "12px 10px",
        boxSizing: "border-box",
      }}
    >
      <CardBusy
        active={cold || soft}
        cover={cold}
        label={cold ? "Loading podium…" : "Updating for date range…"}
      />
      {!cold && top3.length > 0 ? (
        <DashboardPodium top3={top3} />
      ) : (
        !cold && (
          <div className="cs-emptyframe" style={{ minHeight: 220 }}>
            <div className="cs-empty">
              <b>No scores in this range</b>
              Pick another date filter — or wait for the day&apos;s scores to land.
            </div>
          </div>
        )
      )}
    </div>
  );
};

ScopedPodium.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  settings: PropTypes.object,
  workspaceId: PropTypes.string,
  boardWs: PropTypes.string,
  boardWindow: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  boardCustom: PropTypes.object,
  inheritWorkspaceIds: PropTypes.arrayOf(PropTypes.string),
  periodIdx: PropTypes.number,
};

ScopedPodium.defaultProps = {
  settings: {},
  workspaceId: null,
  boardWs: null,
  boardWindow: null,
  boardCustom: null,
  inheritWorkspaceIds: [],
  periodIdx: 3,
};

export default ScopedPodium;
